import { type Format, formatSelection } from "../formatting.ts";
import type { Span } from "../highlight.ts";

/** Formatting shortcuts, taken with Ctrl or ⌘. */
export const formatKeys: Record<Format, string> = { bold: "b", italic: "i", link: "k" };

/**
 * A textarea laid over a styled copy of its own text. The textarea keeps the caret, selection,
 * undo, IME, and spellcheck; its glyphs are transparent so the copy underneath shows through.
 */
export function Editor(textarea: HTMLTextAreaElement) {
  const underlay = <div class="editor-underlay" aria-hidden="true"></div> as HTMLElement;
  underlay.inert = true;
  textarea.classList.add("editor-input");
  const format = (kind: Format) => {
    const edit = formatSelection(
      textarea.value,
      textarea.selectionStart,
      textarea.selectionEnd,
      kind,
    );
    replaceText(textarea, edit.start, edit.end, edit.text);
    textarea.setSelectionRange(...edit.selection);
  };
  textarea.addEventListener("keydown", (event) => {
    const kind = (Object.keys(formatKeys) as Format[]).find((format) =>
      formatKeys[format] === event.key.toLowerCase()
    );
    if (
      !kind || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey ||
      event.isComposing
    ) return;
    event.preventDefault();
    format(kind);
  });
  return {
    format,
    element: <div class="editor">{underlay}{textarea}</div> as HTMLElement,
    render(spans: readonly Span[]) {
      // A trailing newline only takes up a line once something follows it.
      underlay.replaceChildren(...spans.flatMap(spanNodes), "\u200b");
    },
    /** Where a range of the text is drawn, for anchoring popups. */
    rect(start: number, end: number): DOMRect | undefined {
      const from = position(underlay, start, false);
      const to = position(underlay, end, true);
      if (!from || !to) return undefined;
      const range = document.createRange();
      range.setStart(...from);
      range.setEnd(...to);
      return range.getBoundingClientRect();
    },
  };
}

/** Replace a range through the browser's editing pipeline, so the change can be undone. */
export function replaceText(
  textarea: HTMLTextAreaElement,
  start: number,
  end: number,
  text: string,
): void {
  textarea.focus();
  textarea.setSelectionRange(start, end);
  if (document.execCommand("insertText", false, text)) return;
  textarea.setRangeText(text, start, end, "end");
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

function spanNodes(span: Span): Node[] {
  const classes = span.marks.map((mark) => `md-${mark}`).join(" ");
  if (!span.marks.includes("bold") && !span.marks.includes("italic")) {
    return [
      classes ? <span class={classes}>{span.text}</span> : document.createTextNode(span.text),
    ];
  }
  // Bold and italic glyphs are wider than the textarea's. Each word lays out an invisible regular
  // copy, so lines break where the textarea's do, and paints the styled copy over it; the slight
  // overhang lands in the following space or closing delimiter. (Only a word longer than a whole
  // line wraps mid-word, and only there does the styled copy drift.)
  return span.text.split(/(\s+)/).filter(Boolean).map((part) =>
    /\s/.test(part) ? <span class={classes}>{part}</span> : (
      <span class={classes}>
        <span class="md-paint">{part}</span>
        <span class="md-layout">{part}</span>
      </span>
    )
  );
}

function position(root: Node, offset: number, end: boolean): [Node, number] | undefined {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.classList.contains("md-paint")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const length = node.textContent!.length;
    if (end ? offset <= length : offset < length) return [node, offset];
    offset -= length;
  }
  return undefined;
}
